// B-71 — "Enviar consolidado", in a day's detail: the day's report — its summary, everything still
// open and the Matriz in Excel — sent now to the addresses the person types in "Para", or to the
// client's configured recipients when "Para" is left empty (`sendCierreDailyReport`).
//
// While it works the dialog says what it is doing: the backend answers step by step and each one
// is checked off as it ends (`estadoDePasos`) — reading the day, checking the offices, building the
// Matriz, sending — instead of a spinner that could equally be stuck.
//
// It emails people outside this screen, so it asks first. Shown only to whoever holds
// `cierres.export` on the whole client — the Matriz carries every office.
//
// The person can add a comment, which goes first in the email signed with their name, and see the
// email exactly as it will arrive before sending it ("Vista previa", `previewCierreDailyReport`: the
// backend builds the same email and sends nothing; the Matriz is named, not built).
import { useEffect, useState } from "react";
import { ArrowLeft, Check, Circle, Eye, Mail, X } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import {
  previewCierreDailyReport,
  sendCierreDailyReport,
} from "../../../ApiCalls/mantisBusiness";
import { fechaDMY } from "../../../utils/cierrePanel";
import {
  correosEscritos,
  estadoDePasos,
  resultadoDeEnvio,
} from "../../../utils/cierreConsolidado";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const ICONO = {
  pending: <Circle className="h-3.5 w-3.5 text-brinks-muted-foreground" />,
  running: <Spinner size="sm" />,
  done: <Check className="h-3.5 w-3.5 text-brinks-success" />,
  failed: <X className="h-3.5 w-3.5 text-brinks-warning" />,
};

export function EnviarConsolidado({
  token,
  mantisBusinessId,
  clientCode,
  clienteNombre,
  fecha,
  // B-101 — opened from the day's "Más" menu: no button of its own, and `pedido` (a counter) opens it.
  sinBoton = false,
  pedido = 0,
}) {
  const [abierto, setAbierto] = useState(false);
  const [para, setPara] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [comentario, setComentario] = useState("");
  const [vista, setVista] = useState(null);
  const [previsualizando, setPrevisualizando] = useState(false);

  const { correos, invalidos } = correosEscritos(para);
  const pasos = estadoDePasos(eventos);
  const mostrarPasos = enviando || eventos.length > 0;

  const abrir = () => {
    setResultado(null);
    setEventos([]);
    setVista(null);
    setAbierto(true);
  };

  useEffect(() => {
    if (pedido) abrir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido]);

  const previsualizar = async () => {
    setPrevisualizando(true);
    setResultado(null);
    const response = await previewCierreDailyReport(
      token,
      mantisBusinessId,
      clientCode,
      fecha,
      comentario,
    ).catch(() => null);
    setPrevisualizando(false);
    if (response?.success) setVista(response.data);
    else setResultado(resultadoDeEnvio(response));
  };

  const enviar = async () => {
    setEnviando(true);
    setResultado(null);
    setEventos([]);
    const response = await sendCierreDailyReport(
      token,
      mantisBusinessId,
      clientCode,
      fecha,
      correos,
      (evento) => setEventos((actuales) => [...actuales, evento]),
      comentario,
    ).catch(() => null);
    setResultado(resultadoDeEnvio(response));
    setEnviando(false);
  };

  return (
    <>
      {sinBoton ? null : (
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          onClick={abrir}
        >
          <Mail className="h-4 w-4 shrink-0" />{" "}
          <span className="truncate">Enviar consolidado</span>
        </Button>
      )}
      <Dialog
        open={abierto}
        onOpenChange={(open) => !enviando && setAbierto(open)}
      >
        <DialogContent
          className={cn(
            "brinks-scope font-redhat",
            vista ? "max-w-3xl" : "max-w-md",
          )}
        >
          <DialogHeader className="text-left">
            <div className="flex min-w-0 items-center gap-1">
              <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                Enviar el consolidado del {fechaDMY(fecha)}
              </DialogTitle>
              <BotonGuia guiaId="dialogo-enviar-consolidado" titulo="Enviar consolidado" texto="Envía al cliente el resumen del día, lo que sigue pendiente y la Matriz de Conciliación en Excel." />
            </div>
            <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
              Se envía el resumen del día, lo que sigue pendiente y la Matriz de
              Conciliación en Excel de {clienteNombre || clientCode}.
            </DialogDescription>
          </DialogHeader>

          {vista ? (
            <div className="grid gap-1.5">
              <p
                className="truncate text-[12.5px] text-brinks-foreground"
                title={vista.subject}
              >
                <span className="text-brinks-muted-foreground">Asunto: </span>
                {vista.subject}
              </p>
              {/* The email exactly as it will arrive. `sandbox` with nothing allowed: the HTML is the
                  backend's, but an email body has no business running anything here. */}
              <iframe
                {...guia("Vista previa", "El correo tal como va a llegar. \"Volver a editar\" conserva lo que escribiste.")}
                title="Vista previa del correo"
                srcDoc={vista.html}
                sandbox=""
                className="h-[60vh] w-full rounded-[8px] border bg-white"
              />
            </div>
          ) : (
            <>
              <div className="grid gap-1">
                <label
                  htmlFor="enviar-consolidado-para"
                  className="text-[12px] font-medium text-brinks-muted-foreground"
                >
                  Para
                </label>
                <textarea
                  {...guia("Para", "Los correos a los que se envía, separados por comas. Vacío, se envía a los destinatarios configurados del cliente.")}
                  id="enviar-consolidado-para"
                  rows={2}
                  value={para}
                  disabled={enviando}
                  onChange={(e) => setPara(e.target.value)}
                  placeholder="correo@empresa.com, otro@empresa.com"
                  className="min-h-[60px] w-full resize-y rounded-[8px] border bg-card px-2.5 py-1.5 text-[12.5px] text-brinks-foreground outline-none focus:border-brinks-border-strong"
                />
                <span
                  className={cn(
                    "text-[11.5px]",
                    invalidos.length
                      ? "text-brinks-danger"
                      : "text-brinks-muted-foreground",
                  )}
                >
                  {invalidos.length
                    ? `No es un correo válido: ${invalidos.join(", ")}`
                    : "Separa los correos con comas. Si lo dejas vacío, se envía a los destinatarios configurados del cliente."}
                </span>
              </div>

              <div className="grid gap-1">
                <label
                  htmlFor="enviar-consolidado-comentario"
                  className="text-[12px] font-medium text-brinks-muted-foreground"
                >
                  Comentario (opcional)
                </label>
                <textarea
                  {...guia("Comentario", "Opcional: va al inicio del correo, firmado con tu nombre.")}
                  id="enviar-consolidado-comentario"
                  rows={3}
                  maxLength={2000}
                  value={comentario}
                  disabled={enviando}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Lo que quieras que lean primero, por ejemplo: revisar la oficina 203 antes del mediodía."
                  className="min-h-[72px] w-full resize-y rounded-[8px] border bg-card px-2.5 py-1.5 text-[12.5px] text-brinks-foreground outline-none focus:border-brinks-border-strong"
                />
                <span className="text-[11.5px] text-brinks-muted-foreground">
                  Va al inicio del correo, con tu nombre.
                </span>
              </div>
            </>
          )}

          {mostrarPasos ? (
            <ol
              aria-label="Progreso del envío"
              className="grid gap-1.5 rounded-[8px] border bg-brinks-surface px-3 py-2"
            >
              {pasos.map((p) => (
                <li
                  key={p.paso}
                  data-estado={p.estado}
                  className="flex items-start gap-2 text-[12.5px]"
                >
                  <span className="mt-[2px] grid h-4 w-4 shrink-0 place-items-center">
                    {ICONO[p.estado]}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={cn(
                        p.estado === "pending"
                          ? "text-brinks-muted-foreground"
                          : "text-brinks-foreground",
                      )}
                    >
                      {p.titulo}
                    </span>
                    {p.detalle ? (
                      <span className="block text-[11.5px] text-brinks-muted-foreground">
                        {p.detalle}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ol>
          ) : null}

          {resultado ? (
            <p
              role={resultado.ok ? "status" : "alert"}
              className={cn(
                "rounded-[8px] border px-2.5 py-1.5 text-[12.5px]",
                resultado.ok
                  ? "border-brinks-success-border bg-brinks-success-soft text-brinks-success"
                  : "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
              )}
            >
              {resultado.texto}
            </p>
          ) : null}
          <DialogFooter className="gap-2">
            {resultado?.ok ? (
              <Button className="h-9" onClick={() => setAbierto(false)}>
                Cerrar
              </Button>
            ) : (
              <>
                {vista ? (
                  <Button
                    variant="outline"
                    className="h-9 gap-1.5"
                    disabled={enviando}
                    onClick={() => setVista(null)}
                  >
                    <ArrowLeft className="h-4 w-4" /> Volver a editar
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="h-9"
                      disabled={enviando || previsualizando}
                      onClick={() => setAbierto(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="outline"
                      className="h-9 gap-1.5"
                      disabled={
                        enviando || previsualizando || invalidos.length > 0
                      }
                      onClick={previsualizar}
                    >
                      {previsualizando ? (
                        <Spinner size="sm" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}{" "}
                      Vista previa
                    </Button>
                  </>
                )}
                <Button
                  className="h-9 gap-1.5"
                  {...guia("Enviar", "Envía el correo. Mientras trabaja te muestra cada paso: leer el día, revisar las oficinas, armar la Matriz y enviar.")}
                  disabled={enviando || previsualizando || invalidos.length > 0}
                  onClick={enviar}
                >
                  {enviando ? (
                    <Spinner size="sm" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}{" "}
                  {enviando ? "Enviando…" : "Enviar"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EnviarConsolidado;
